import React, { useMemo, useState } from "react";

import { faChevronDown } from "@fortawesome/pro-solid-svg-icons/faChevronDown";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Dropdown from "antd/es/dropdown";
import Menu from "antd/es/menu";
import { defineMessages, useIntl } from "react-intl";

import {
  EntitiesSupportingGroups,
  EntitiesSupportingRoutes,
} from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import { Button } from "@mapmycustomers/ui";

import styles from "./AddRemoveButton.module.scss";
import GroupsPanel from "./Modals/GroupsModal";
import RoutesModal from "./Modals/RouteModal";

interface Props {
  disabled?: boolean;
  onReloadData?: () => void;
  selectedGroupEntities: Set<EntitiesSupportingGroups>;
  selectedRouteEntities: Set<EntitiesSupportingRoutes>;
}

const messages = defineMessages({
  groups: {
    id: "view.selectBar.addRemove.context.groups",
    defaultMessage: "Add to / remove from groups",
    description: "Groups context label",
  },
  routes: {
    id: "view.selectBar.addRemove.context.routes",
    defaultMessage: "Add to / remove from routes",
    description: "Routes context label",
  },
});

const AddRemoveButton: React.FC<Props> = ({
  disabled,
  onReloadData,
  selectedGroupEntities,
  selectedRouteEntities,
}) => {
  const intl = useIntl();
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const onOpenChange = (openKeys: React.Key[]) => setOpenKeys(openKeys as string[]);
  const [routeModalVisible, showRouteModal, hideRouteModal] = useBoolean();
  const [groupModalVisible, showGroupModal, hideGroupModal] = useBoolean();

  const groupEntities = useMemo<EntitiesSupportingGroups[]>(
    () => Array.from(selectedGroupEntities),
    [selectedGroupEntities]
  );

  const routeEntities = useMemo<EntitiesSupportingRoutes[]>(
    () => Array.from(selectedRouteEntities),
    [selectedRouteEntities]
  );

  return (
    <>
      <Dropdown
        className={styles.dropdown}
        disabled={disabled}
        overlay={
          <Menu
            className={styles.menu}
            expandIcon={<span />}
            onOpenChange={onOpenChange}
            openKeys={openKeys}
            triggerSubMenuAction="click"
          >
            <Menu.Item onClick={showGroupModal}>{intl.formatMessage(messages.groups)}</Menu.Item>
            <Menu.Item onClick={showRouteModal}>{intl.formatMessage(messages.routes)}</Menu.Item>
          </Menu>
        }
        trigger={["click"]}
      >
        <Button
          className={styles.active}
          rightIcon={<FontAwesomeIcon icon={faChevronDown} />}
          type="filled"
        >
          {intl.formatMessage({
            id: "view.selectBar.button.addRemoveAction",
            defaultMessage: "Add / remove",
            description: "Add / remove button on a selected entities bar",
          })}
        </Button>
      </Dropdown>
      {routeModalVisible && (
        <RoutesModal entities={routeEntities} onHide={hideRouteModal} onReloadData={onReloadData} />
      )}
      {groupModalVisible && (
        <GroupsPanel entities={groupEntities} onHide={hideGroupModal} onReloadData={onReloadData} />
      )}
    </>
  );
};

export default AddRemoveButton;
